import indexedDbFileStorage from './indexedDbFileStorage';

/**
 * VirtualFileSystem - Executes bash commands on IndexedDB files
 * 
 * Workflow:
 * 1. Load file metadata (paths only) from IndexedDB
 * 2. Execute bash commands using browser-compatible shell emulator
 * 3. Load file content on-demand when accessed
 * 4. Detect which files were modified
 * 5. Return output and modified files list
 * 
 * Note: Uses custom shell command implementation for browser compatibility
 * Optimized for memory: only loads file content when needed
 */
class VirtualFileSystem {
  constructor() {
    this.projectId = null;
    this.serviceName = null;
    this.files = {}; // In-memory file system: path -> content (lazy loaded)
    this.filePaths = []; // List of all file paths (loaded upfront)
    this.filesSnapshot = {}; // Original content for change detection
    this.loadedFiles = new Set(); // Track which files have been loaded
  }

  /**
   * Load file metadata from IndexedDB (paths only, content loaded on-demand)
   */
  async loadFromIndexedDB(projectId, serviceName) {
    this.projectId = projectId;
    this.serviceName = serviceName;
    
    // Get all files from IndexedDB
    const filesFromDb = await indexedDbFileStorage.getAllFiles(projectId, serviceName);
    
    // Store only paths, not content
    this.files = {};
    this.filePaths = [];
    this.filesSnapshot = {};
    this.loadedFiles.clear();
    
    filesFromDb.forEach(file => {
      if (file.state !== 'deleted') {
        this.filePaths.push(file.filePath);
        // Don't load content yet, just mark as available
        this.files[file.filePath] = null; // Placeholder
      }
    });
    
    return this.filePaths.length;
  }

  /**
   * Load file content on-demand from IndexedDB
   */
  async _loadFileContent(filePath) {
    // Already loaded?
    if (this.loadedFiles.has(filePath)) {
      return this.files[filePath];
    }
    
    // File doesn't exist?
    if (!this.filePaths.includes(filePath)) {
      return null;
    }
    
    // Load from IndexedDB
    const file = await indexedDbFileStorage.getFile(this.projectId, this.serviceName, filePath);
    if (file && file.state !== 'deleted') {
      this.files[filePath] = file.content;
      this.filesSnapshot[filePath] = {
        content: file.content,
        exists: true
      };
      this.loadedFiles.add(filePath);
      return file.content;
    }
    
    return null;
  }

  /**
   * Execute a bash script using browser-compatible commands
   * Returns: {stdout, stderr, exit_code, modifiedFiles}
   */
  async executeScript(script) {
    const result = {
      stdout: '',
      stderr: '',
      exit_code: 0,
      modifiedFiles: [],
      error: null
    };
    
    try {
      // Parse and execute the script (now async to support lazy loading)
      const output = await this._executeCommand(script);
      result.stdout = output;
      
      // Detect modified files
      result.modifiedFiles = await this._detectModifiedFiles();
      
    } catch (error) {
      result.stderr = error.message;
      result.exit_code = 1;
      result.error = error.message;
    }
    
    return result;
  }

  /**
   * Execute a single command or pipeline (async for lazy loading)
   */
  async _executeCommand(command) {
    const trimmedCmd = command.trim();
    
    // Handle pipes
    if (trimmedCmd.includes('|')) {
      return await this._executePipeline(trimmedCmd);
    }
    
    // Parse command and args
    const parts = this._parseCommand(trimmedCmd);
    if (parts.length === 0) return '';
    
    const cmd = parts[0];
    const args = parts.slice(1);
    
    // Execute based on command type
    switch (cmd) {
      case 'grep':
        return await this._grep(args);
      case 'find':
        return this._find(args); // find doesn't need content
      case 'cat':
        return await this._cat(args);
      case 'ls':
        return this._ls(args); // ls doesn't need content
      case 'wc':
        return await this._wc(args);
      case 'head':
        return await this._head(args);
      case 'tail':
        return await this._tail(args);
      case 'echo':
        return this._echo(args); // echo doesn't need files
      case 'xargs':
        return await this._xargs(args);
      case 'sed':
        return await this._sed(args);
      case 'awk':
        return await this._awk(args);
      case 'cut':
        return this._cut(args);
      case 'tree':
        return this._tree(args);
      case 'du':
        return await this._du(args);
      case 'basename':
        return this._basename(args);
      case 'dirname':
        return this._dirname(args);
      case 'diff':
        return await this._diff(args);
      case 'tr':
        return this._tr(args);
      case 'tee':
        return this._tee(args);
      case 'more':
      case 'less':
        return await this._more(args);
      default:
        throw new Error(`Command not supported: ${cmd}`);
    }
  }

  /**
   * Split a pipeline string into individual commands, respecting quoted strings
   * and escaped pipes (\|). A bare | is a pipe; \| or a | inside quotes is literal.
   */
  _splitPipeline(pipeline) {
    const commands = [];
    let current = '';
    let inQuotes = false;
    let quoteChar = '';

    for (let i = 0; i < pipeline.length; i++) {
      const char = pipeline[i];

      // Toggle quote state
      if ((char === '"' || char === "'") && !inQuotes) {
        inQuotes = true;
        quoteChar = char;
        current += char;
        continue;
      }
      if (char === quoteChar && inQuotes) {
        inQuotes = false;
        quoteChar = '';
        current += char;
        continue;
      }

      // Escaped pipe (\|) → keep as literal, do NOT split
      if (char === '\\' && i + 1 < pipeline.length && pipeline[i + 1] === '|') {
        current += '\\|';
        i++; // skip the next '|'
        continue;
      }

      // Unquoted bare pipe → pipeline boundary
      if (char === '|' && !inQuotes) {
        commands.push(current.trim());
        current = '';
        continue;
      }

      current += char;
    }

    if (current.trim()) commands.push(current.trim());
    return commands;
  }

  /**
   * Execute a pipeline of commands (async)
   */
  async _executePipeline(pipeline) {
    const commands = this._splitPipeline(pipeline);
    let input = '';
    
    for (let i = 0; i < commands.length; i++) {
      const cmd = commands[i];
      const output = await this._executeCommandWithInput(cmd, input);
      input = output;
    }
    
    return input;
  }

  /**
   * Execute command with input from previous command (async)
   */
  async _executeCommandWithInput(command, input) {
    const parts = this._parseCommand(command);
    if (parts.length === 0) return input;
    
    const cmd = parts[0];
    const args = parts.slice(1);
    
    switch (cmd) {
      case 'wc':
        return this._wcFromInput(input, args);
      case 'grep':
        return this._grepFromInput(input, args);
      case 'head':
        return this._headFromInput(input, args);
      case 'tail':
        return this._tailFromInput(input, args);
      case 'sort':
        return this._sortFromInput(input, args);
      case 'uniq':
        return this._uniqFromInput(input);
      case 'xargs':
        return await this._xargsFromInput(input, args);
      case 'sed':
        return this._sedFromInput(input, args);
      case 'awk':
        return this._awkFromInput(input, args);
      case 'cut':
        return this._cutFromInput(input, args);
      case 'tr':
        return this._trFromInput(input, args);
      case 'tee':
        return this._teeFromInput(input, args);
      default:
        // If command doesn't accept stdin, execute normally
        return await this._executeCommand(command);
    }
  }

  /**
   * Parse command string into array of arguments
   */
  _parseCommand(command) {
    const parts = [];
    let current = '';
    let inQuotes = false;
    let quoteChar = '';
    
    for (let i = 0; i < command.length; i++) {
      const char = command[i];
      
      if ((char === '"' || char === "'") && !inQuotes) {
        inQuotes = true;
        quoteChar = char;
      } else if (char === quoteChar && inQuotes) {
        inQuotes = false;
        quoteChar = '';
      } else if (char === ' ' && !inQuotes) {
        if (current) {
          parts.push(current);
          current = '';
        }
      } else {
        current += char;
      }
    }
    
    if (current) {
      parts.push(current);
    }
    
    return parts;
  }

  /**
   * Grep command - search files for pattern (async with lazy loading)
   */
  async _grep(args) {
    if (args.length === 0) return '';
    
    // Parse grep flags
    let recursive = false;
    let caseInsensitive = false;
    let showLineNumbers = false;
    let includePatterns = [];  // Support multiple --include flags
    let pattern = null;
    let filesToSearch = [];
    
    for (let i = 0; i < args.length; i++) {
      const arg = args[i];
      
      if (arg === '-r' || arg === '-R' || arg === '--recursive') {
        recursive = true;
      } else if (arg === '-i' || arg === '--ignore-case') {
        caseInsensitive = true;
      } else if (arg === '-n' || arg === '--line-number') {
        showLineNumbers = true;
      } else if (arg === '-E' || arg === '--extended-regexp') {
        // Extended regex — JS RegExp already supports ERE syntax, no-op flag
      } else if (arg === '-e' || arg === '--regexp') {
        // Explicit pattern: -e PATTERN (allows multiple, here we support one)
        if (i + 1 < args.length) pattern = args[++i];
      } else if (arg === '--include' || arg.startsWith('--include=')) {
        if (arg.startsWith('--include=')) {
          includePatterns.push(arg.substring('--include='.length));
        } else if (i + 1 < args.length) {
          includePatterns.push(args[++i]);
        }
      } else if (!pattern) {
        // First non-flag argument is the pattern
        pattern = arg;
      } else {
        // Remaining arguments are files/directories to search
        filesToSearch.push(arg);
      }
    }
    
    if (!pattern) return '';
    
    // If no files specified, search all files
    if (filesToSearch.length === 0) {
      filesToSearch = [...this.filePaths];
    } else {
      // Expand wildcards and directories
      filesToSearch = this._expandWildcards(filesToSearch);
    }
    
    // Apply include filters if specified (OR logic - match any pattern)
    if (includePatterns.length > 0) {
      const filtered = filesToSearch.filter(path => {
        return includePatterns.some(includePattern => {
          // Remove quotes from pattern
          const cleanPattern = includePattern.replace(/^["']|["']$/g, '');
          // Skip filtering if pattern is just "*" (match all)
          if (cleanPattern === '*') return true;
          return this._matchPattern(path, cleanPattern);
        });
      });
      filesToSearch = filtered;
    }
    
    // Build a RegExp from the pattern.
    // Convert BRE alternation (\|) to JS alternation (|) so that e.g.
    // grep 'foo\|bar' works identically to grep -E 'foo|bar'.
    let regexSource = pattern.replace(/\\\|/g, '|');
    let flags = caseInsensitive ? 'i' : '';
    let searchRegex;
    try {
      searchRegex = new RegExp(regexSource, flags);
    } catch (e) {
      // Fall back to literal string match if the pattern is not valid regex
      searchRegex = new RegExp(regexSource.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), flags);
    }

    const results = [];
    // Load and search files on-demand
    for (const filePath of filesToSearch) {
      const content = await this._loadFileContent(filePath);
      if (!content) continue;

      const lines = content.split('\n');
      lines.forEach((line, index) => {
        if (searchRegex.test(line)) {
          if (showLineNumbers) {
            results.push(`${filePath}:${index + 1}:${line}`);
          } else {
            results.push(`${filePath}:${line}`);
          }
        }
      });
    }

    return results.join('\n');
  }

  /**
   * Grep from input (stdin pipeline stage)
   */
  _grepFromInput(input, args) {
    if (args.length === 0) return input;

    let caseInsensitive = false;
    let pattern = null;

    for (let i = 0; i < args.length; i++) {
      const arg = args[i];
      if (arg === '-i' || arg === '--ignore-case') {
        caseInsensitive = true;
      } else if (arg === '-E' || arg === '-e' || arg === '--extended-regexp') {
        // next arg is pattern for -e, flag only for -E
        if (arg === '-e' && i + 1 < args.length) pattern = args[++i];
      } else if (!pattern) {
        pattern = arg;
      }
    }

    if (!pattern) return input;

    // BRE \| → JS |
    const regexSource = pattern.replace(/\\\|/g, '|');
    let searchRegex;
    try {
      searchRegex = new RegExp(regexSource, caseInsensitive ? 'i' : '');
    } catch (e) {
      searchRegex = new RegExp(regexSource.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), caseInsensitive ? 'i' : '');
    }

    return input.split('\n').filter(line => searchRegex.test(line)).join('\n');
  }

  /**
   * Find command - list files (doesn't need content, so no lazy loading)
   */
  _find(args) {
    // Use filePaths instead of Object.keys(this.files)
    const filePaths = [...this.filePaths];
    
    // Handle no args or just directory
    if (args.length === 0 || (args.length === 1 && args[0] === '.')) {
      return filePaths.join('\n');
    }
    
    // Collect all patterns to match (handles -o operator for OR conditions)
    const patterns = [];
    for (let i = 0; i < args.length; i++) {
      if (args[i] === '-name' && args[i + 1]) {
        patterns.push(args[i + 1]);
        i++; // Skip the pattern itself
      }
    }
    
    // If we found patterns, filter by them (OR logic)
    if (patterns.length > 0) {
      const filtered = filePaths.filter(path => 
        patterns.some(pattern => this._matchPattern(path, pattern))
      );
      return filtered.join('\n');
    }
    
    // No recognized options - return all files
    return filePaths.join('\n');
  }

  /**
   * Cat command - display file contents (async with lazy loading)
   */
  async _cat(args) {
    const results = [];
    
    for (const filePath of args) {
      const content = await this._loadFileContent(filePath);
      if (content) {
        results.push(content);
      }
    }
    
    return results.join('\n');
  }

  /**
   * Ls command - list files (doesn't need content)
   */
  _ls(args) {
    return this.filePaths.join('\n');
  }

  /**
   * Wc command - count lines, words, characters (async with lazy loading)
   */
  async _wc(args) {
    const hasL = args.includes('-l');
    const hasW = args.includes('-w');
    const hasC = args.includes('-c');
    
    const filesToCount = args.filter(arg => !arg.startsWith('-'));
    const expandedFiles = this._expandWildcards(filesToCount.length > 0 ? filesToCount : this.filePaths);
    
    let totalLines = 0;
    let totalWords = 0;
    let totalChars = 0;
    
    for (const filePath of expandedFiles) {
      const content = await this._loadFileContent(filePath);
      if (!content) continue;
      
      const lines = content.split('\n').length;
      const words = content.split(/\s+/).filter(w => w).length;
      const chars = content.length;
      
      totalLines += lines;
      totalWords += words;
      totalChars += chars;
    }
    
    if (hasL) return totalLines.toString();
    if (hasW) return totalWords.toString();
    if (hasC) return totalChars.toString();
    
    return `${totalLines} ${totalWords} ${totalChars}`;
  }

  /**
   * Wc from input
   */
  _wcFromInput(input, args) {
    const hasL = args.includes('-l');
    
    if (hasL) {
      const lines = input.split('\n').filter(line => line.trim()).length;
      return lines.toString();
    }
    
    const lines = input.split('\n').length;
    const words = input.split(/\s+/).filter(w => w).length;
    const chars = input.length;
    
    return `${lines} ${words} ${chars}`;
  }

  /**
   * Head command - show first N lines (async with lazy loading)
   */
  async _head(args) {
    const n = args.includes('-n') ? parseInt(args[args.indexOf('-n') + 1]) : 10;
    const fileArgs = args.filter(arg => !arg.startsWith('-') && arg !== n.toString());
    
    if (fileArgs.length === 0) return '';
    
    const filePath = fileArgs[0];
    const content = await this._loadFileContent(filePath);
    if (!content) return '';
    
    const lines = content.split('\n');
    return lines.slice(0, n).join('\n');
  }

  /**
   * Head from input
   */
  _headFromInput(input, args) {
    const n = args.includes('-n') ? parseInt(args[args.indexOf('-n') + 1]) : 10;
    const lines = input.split('\n');
    return lines.slice(0, n).join('\n');
  }

  /**
   * Tail command - show last N lines (async with lazy loading)
   */
  async _tail(args) {
    const n = args.includes('-n') ? parseInt(args[args.indexOf('-n') + 1]) : 10;
    const fileArgs = args.filter(arg => !arg.startsWith('-') && arg !== n.toString());
    
    if (fileArgs.length === 0) return '';
    
    const filePath = fileArgs[0];
    const content = await this._loadFileContent(filePath);
    if (!content) return '';
    
    const lines = content.split('\n');
    return lines.slice(-n).join('\n');
  }

  /**
   * Tail from input
   */
  _tailFromInput(input, args) {
    const n = args.includes('-n') ? parseInt(args[args.indexOf('-n') + 1]) : 10;
    const lines = input.split('\n');
    return lines.slice(-n).join('\n');
  }

  /**
   * Sort from input
   */
  _sortFromInput(input) {
    const lines = input.split('\n');
    return lines.sort().join('\n');
  }

  /**
   * Uniq from input
   */
  _uniqFromInput(input) {
    const lines = input.split('\n');
    const unique = [...new Set(lines)];
    return unique.join('\n');
  }

  /**
   * Echo command
   */
  _echo(args) {
    return args.join(' ');
  }

  /**
   * Xargs command - build and execute commands from stdin
   * Usage: find . -name "*.js" | xargs grep "TODO"
   */
  async _xargs(args) {
    // xargs needs stdin, return error if called directly
    throw new Error('xargs requires input from stdin (use with pipe)');
  }

  async _xargsFromInput(input, args) {
    if (args.length === 0) return '';
    
    // Split input into arguments
    const inputArgs = input.split(/\s+/).filter(arg => arg.trim());
    if (inputArgs.length === 0) return '';
    
    // Build command with input arguments
    const command = args.join(' ');
    const results = [];
    
    // Execute command for each input argument
    for (const inputArg of inputArgs) {
      const fullCommand = `${command} ${inputArg}`;
      try {
        const output = await this._executeCommand(fullCommand);
        if (output) results.push(output);
      } catch (error) {
        // Continue with other arguments on error
      }
    }
    
    return results.join('\n');
  }

  /**
   * Sed command - stream editor for text transformation
   * Supports: s/pattern/replacement/flags, -n, p, d, line ranges
   */
  async _sed(args) {
    if (args.length === 0) return '';
    
    // Parse sed options
    let quiet = false;
    let scriptIndex = 0;
    
    if (args[0] === '-n') {
      quiet = true;
      scriptIndex = 1;
    }
    
    if (scriptIndex >= args.length) return '';
    
    const script = args[scriptIndex];
    const files = args.slice(scriptIndex + 1);
    
    // If no files specified, need stdin
    if (files.length === 0) {
      throw new Error('sed requires input files or stdin');
    }
    
    const results = [];
    for (const filePath of this._expandWildcards(files)) {
      const content = await this._loadFileContent(filePath);
      if (!content) continue;
      
      const processed = this._processSed(content, script, quiet);
      if (processed) results.push(processed);
    }
    
    return results.join('\n');
  }

  _sedFromInput(input, args) {
    if (args.length === 0) return input;
    
    let quiet = false;
    let scriptIndex = 0;
    
    if (args[0] === '-n') {
      quiet = true;
      scriptIndex = 1;
    }
    
    if (scriptIndex >= args.length) return input;
    
    const script = args[scriptIndex];
    return this._processSed(input, script, quiet);
  }

  _processSed(text, script, quiet) {
    const lines = text.split('\n');
    const output = [];
    
    // Handle different sed commands
    // Line range: '10,20p' - print lines 10-20
    if (/^(\d+),(\d+)p$/.test(script)) {
      const match = script.match(/^(\d+),(\d+)p$/);
      const start = parseInt(match[1]) - 1;
      const end = parseInt(match[2]);
      return lines.slice(start, end).join('\n');
    }
    
    // Single line: '10p' - print line 10
    if (/^(\d+)p$/.test(script)) {
      const lineNum = parseInt(script.match(/^(\d+)p$/)[1]) - 1;
      return lines[lineNum] || '';
    }
    
    // Delete: 'd' or line range with 'd'
    if (script === 'd') {
      return ''; // Delete all lines
    }
    
    // Substitution: s/pattern/replacement/flags
    if (script.startsWith('s/')) {
      const parts = script.split('/');
      if (parts.length >= 3) {
        const pattern = parts[1];
        const replacement = parts[2];
        const flags = parts[3] || '';
        
        const global = flags.includes('g');
        const caseInsensitive = flags.includes('i');
        
        const regex = new RegExp(pattern, (global ? 'g' : '') + (caseInsensitive ? 'i' : ''));
        
        lines.forEach(line => {
          const newLine = line.replace(regex, replacement);
          output.push(newLine);
        });
        
        return output.join('\n');
      }
    }
    
    // Default: return as-is
    return quiet ? '' : text;
  }

  /**
   * Awk command - pattern scanning and processing
   * Supports: field extraction, -F delimiter, patterns
   */
  async _awk(args) {
    if (args.length === 0) return '';
    
    // Parse awk options
    let delimiter = /\s+/;
    let script = '';
    let files = [];
    
    for (let i = 0; i < args.length; i++) {
      if (args[i] === '-F' && i + 1 < args.length) {
        delimiter = new RegExp(args[i + 1].replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        i++;
      } else if (!script) {
        script = args[i];
      } else {
        files.push(args[i]);
      }
    }
    
    if (!script) return '';
    
    // If no files, need stdin
    if (files.length === 0) {
      throw new Error('awk requires input files or stdin');
    }
    
    const results = [];
    for (const filePath of this._expandWildcards(files)) {
      const content = await this._loadFileContent(filePath);
      if (!content) continue;
      
      const processed = this._processAwk(content, script, delimiter);
      if (processed) results.push(processed);
    }
    
    return results.join('\n');
  }

  _awkFromInput(input, args) {
    if (args.length === 0) return input;
    
    let delimiter = /\s+/;
    let script = '';
    
    for (let i = 0; i < args.length; i++) {
      if (args[i] === '-F' && i + 1 < args.length) {
        delimiter = new RegExp(args[i + 1].replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        i++;
      } else if (!script) {
        script = args[i];
      }
    }
    
    if (!script) return input;
    
    return this._processAwk(input, script, delimiter);
  }

  _processAwk(text, script, delimiter) {
    const lines = text.split('\n');
    const output = [];
    
    // Extract field references like $1, $2, $3
    const fieldRefs = script.match(/\$(\d+)/g) || [];
    
    lines.forEach(line => {
      if (!line.trim()) return;
      
      const fields = line.split(delimiter);
      let result = script;
      
      // Replace field references
      fieldRefs.forEach(ref => {
        const fieldNum = parseInt(ref.substring(1));
        const fieldValue = fields[fieldNum - 1] || '';
        result = result.replace(new RegExp('\\' + ref, 'g'), fieldValue);
      });
      
      // Handle {print ...} syntax
      if (result.includes('{print')) {
        const match = result.match(/\{print\s+(.+)\}/);
        if (match) {
          result = match[1];
          // Evaluate field references again in print statement
          fieldRefs.forEach(ref => {
            const fieldNum = parseInt(ref.substring(1));
            const fieldValue = fields[fieldNum - 1] || '';
            result = result.replace(new RegExp('\\' + ref, 'g'), fieldValue);
          });
        }
      }
      
      output.push(result);
    });
    
    return output.join('\n');
  }

  /**
   * Cut command - extract columns from text
   * Supports: -d delimiter, -f fields, -c characters
   */
  _cut(args) {
    // cut needs stdin or files
    throw new Error('cut requires input from stdin or files');
  }

  _cutFromInput(input, args) {
    if (args.length === 0) return input;
    
    let delimiter = '\t';
    let fields = [];
    let chars = null;
    
    for (let i = 0; i < args.length; i++) {
      if (args[i] === '-d' && i + 1 < args.length) {
        delimiter = args[i + 1];
        i++;
      } else if (args[i] === '-f' && i + 1 < args.length) {
        // Parse field specification: 1,3,5 or 1-3 or 1,3-5
        fields = this._parseFieldSpec(args[i + 1]);
        i++;
      } else if (args[i] === '-c' && i + 1 < args.length) {
        chars = this._parseFieldSpec(args[i + 1]);
        i++;
      }
    }
    
    const lines = input.split('\n');
    const output = [];
    
    lines.forEach(line => {
      if (!line) return;
      
      if (chars) {
        // Character mode
        const selected = chars.map(c => line[c - 1] || '').join('');
        output.push(selected);
      } else if (fields.length > 0) {
        // Field mode
        const parts = line.split(delimiter);
        const selected = fields.map(f => parts[f - 1] || '').join(delimiter);
        output.push(selected);
      }
    });
    
    return output.join('\n');
  }

  _parseFieldSpec(spec) {
    const fields = [];
    const parts = spec.split(',');
    
    parts.forEach(part => {
      if (part.includes('-')) {
        const [start, end] = part.split('-').map(n => parseInt(n));
        for (let i = start; i <= end; i++) {
          fields.push(i);
        }
      } else {
        fields.push(parseInt(part));
      }
    });
    
    return fields;
  }

  /**
   * Tree command - display directory structure
   * Supports: -L level, --include pattern
   */
  _tree(args) {
    let maxLevel = Infinity;
    let includePattern = null;
    
    for (let i = 0; i < args.length; i++) {
      if (args[i] === '-L' && i + 1 < args.length) {
        maxLevel = parseInt(args[i + 1]);
        i++;
      } else if (args[i] === '--include' && i + 1 < args.length) {
        includePattern = args[i + 1].replace(/^["']|["']$/g, '');
        i++;
      }
    }
    
    // Build tree structure
    const tree = {};
    this.filePaths.forEach(path => {
      // Apply include filter
      if (includePattern && !this._matchPattern(path, includePattern)) {
        return;
      }
      
      const parts = path.split('/');
      let current = tree;
      
      parts.forEach((part, index) => {
        if (index >= maxLevel) return;
        
        if (!current[part]) {
          current[part] = {};
        }
        current = current[part];
      });
    });
    
    // Format tree output
    const formatTree = (node, prefix = '', isLast = true) => {
      const lines = [];
      const entries = Object.keys(node).sort();
      
      entries.forEach((key, index) => {
        const isLastEntry = index === entries.length - 1;
        const marker = isLastEntry ? '└── ' : '├── ';
        const childPrefix = prefix + (isLastEntry ? '    ' : '│   ');
        
        lines.push(prefix + marker + key);
        
        const children = node[key];
        if (Object.keys(children).length > 0) {
          lines.push(...formatTree(children, childPrefix, isLastEntry));
        }
      });
      
      return lines;
    };
    
    const output = ['.', ...formatTree(tree)];
    return output.join('\n');
  }

  /**
   * Du command - estimate file space usage
   * Supports: -h (human readable), -s (summary)
   */
  async _du(args) {
    const humanReadable = args.includes('-h');
    const summary = args.includes('-s');
    const paths = args.filter(arg => !arg.startsWith('-'));
    
    const filesToCheck = paths.length > 0 
      ? this._expandWildcards(paths)
      : this.filePaths;
    
    let totalSize = 0;
    const sizes = [];
    
    for (const filePath of filesToCheck) {
      const content = await this._loadFileContent(filePath);
      if (!content) continue;
      
      const size = content.length;
      totalSize += size;
      
      if (!summary) {
        const displaySize = humanReadable ? this._formatBytes(size) : size;
        sizes.push(`${displaySize}\t${filePath}`);
      }
    }
    
    if (summary) {
      const displaySize = humanReadable ? this._formatBytes(totalSize) : totalSize;
      return `${displaySize}\ttotal`;
    }
    
    return sizes.join('\n');
  }

  _formatBytes(bytes) {
    if (bytes < 1024) return bytes + 'B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'K';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + 'M';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + 'G';
  }

  /**
   * Basename command - extract filename from path
   */
  _basename(args) {
    if (args.length === 0) return '';
    
    const path = args[0];
    const suffix = args[1];
    
    let basename = path.split('/').pop();
    
    if (suffix && basename.endsWith(suffix)) {
      basename = basename.substring(0, basename.length - suffix.length);
    }
    
    return basename;
  }

  /**
   * Dirname command - extract directory from path
   */
  _dirname(args) {
    if (args.length === 0) return '';
    
    const path = args[0];
    const parts = path.split('/');
    parts.pop();
    
    return parts.join('/') || '.';
  }

  /**
   * Diff command - compare files
   * Basic implementation showing differences
   */
  async _diff(args) {
    if (args.length < 2) return '';
    
    const file1 = args[0];
    const file2 = args[1];
    
    const content1 = await this._loadFileContent(file1);
    const content2 = await this._loadFileContent(file2);
    
    if (!content1 || !content2) return '';
    
    const lines1 = content1.split('\n');
    const lines2 = content2.split('\n');
    
    const output = [];
    output.push(`--- ${file1}`);
    output.push(`+++ ${file2}`);
    
    const maxLines = Math.max(lines1.length, lines2.length);
    
    for (let i = 0; i < maxLines; i++) {
      const line1 = lines1[i];
      const line2 = lines2[i];
      
      if (line1 !== line2) {
        if (line1 !== undefined) {
          output.push(`- ${line1}`);
        }
        if (line2 !== undefined) {
          output.push(`+ ${line2}`);
        }
      }
    }
    
    return output.join('\n');
  }

  /**
   * Tr command - translate or delete characters
   * Supports: character ranges, deletion
   */
  _tr(args) {
    throw new Error('tr requires input from stdin');
  }

  _trFromInput(input, args) {
    if (args.length === 0) return input;
    
    let deleteMode = false;
    let set1 = '';
    let set2 = '';
    
    if (args[0] === '-d') {
      deleteMode = true;
      set1 = args[1] || '';
    } else {
      set1 = args[0] || '';
      set2 = args[1] || '';
    }
    
    // Expand character ranges like a-z
    const expandRange = (str) => {
      let expanded = '';
      for (let i = 0; i < str.length; i++) {
        if (i + 2 < str.length && str[i + 1] === '-') {
          const start = str.charCodeAt(i);
          const end = str.charCodeAt(i + 2);
          for (let code = start; code <= end; code++) {
            expanded += String.fromCharCode(code);
          }
          i += 2;
        } else {
          expanded += str[i];
        }
      }
      return expanded;
    };
    
    set1 = expandRange(set1);
    set2 = expandRange(set2);
    
    if (deleteMode) {
      // Delete characters in set1
      return input.split('').filter(c => !set1.includes(c)).join('');
    } else {
      // Translate set1 to set2
      return input.split('').map(c => {
        const index = set1.indexOf(c);
        return index >= 0 ? (set2[index] || set2[set2.length - 1] || c) : c;
      }).join('');
    }
  }

  /**
   * Tee command - read stdin and write to files and stdout
   */
  _tee(args) {
    throw new Error('tee requires input from stdin');
  }

  _teeFromInput(input, args) {
    // In a browser context, we can't easily write to files
    // Just return the input (simulate writing to stdout)
    return input;
  }

  /**
   * More/Less command - paginate file content
   */
  async _more(args) {
    if (args.length === 0) return '';
    
    const filePath = args[0];
    const content = await this._loadFileContent(filePath);
    
    return content || '';
  }

  /**
   * Expand wildcards in file patterns (uses filePaths, no content loading needed)
   */
  _expandWildcards(patterns) {
    const expanded = [];
    
    for (const pattern of patterns) {
      if (pattern === '*') {
        expanded.push(...this.filePaths);
      } else if (pattern.includes('*')) {
        const matched = this.filePaths.filter(path =>
          this._matchPattern(path, pattern)
        );
        expanded.push(...matched);
      } else {
        expanded.push(pattern);
      }
    }
    
    return [...new Set(expanded)];
  }

  /**
   * Match file path against pattern with wildcards
   */
  _matchPattern(path, pattern) {
    const regexPattern = pattern
      .replace(/\./g, '\\.')
      .replace(/\*/g, '.*')
      .replace(/\?/g, '.');
    
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(path);
  }

  /**
   * Detect which files were modified after script execution
   * Compares current filesystem state with initial snapshot
   * Only flags files that were actually changed, not just loaded
   */
  async _detectModifiedFiles() {
    const modifiedFiles = [];
    
    // Check all files in snapshot (only these were loaded and could be modified)
    for (const [filePath, snapshot] of Object.entries(this.filesSnapshot)) {
      const currentContent = this.files[filePath];
      
      if (currentContent !== undefined && currentContent !== null) {
        // Compare with original content from snapshot
        if (currentContent !== snapshot.content) {
          modifiedFiles.push({
            path: filePath,
            type: 'modified',
            oldContent: snapshot.content,
            newContent: currentContent
          });
        }
      } else if (currentContent === undefined) {
        // File was deleted (removed from this.files)
        modifiedFiles.push({
          path: filePath,
          type: 'deleted',
          oldContent: snapshot.content,
          newContent: null
        });
      }
      // If currentContent === null, file was never loaded, so no change
    }
    
    // Check for new files (content not null and not in snapshot)
    for (const [filePath, content] of Object.entries(this.files)) {
      if (content !== null && !this.filesSnapshot[filePath]) {
        modifiedFiles.push({
          path: filePath,
          type: 'created',
          oldContent: null,
          newContent: content
        });
      }
    }
    
    return modifiedFiles;
  }

  /**
   * Sync modified files back to IndexedDB
   */
  async syncChangesToIndexedDB(modifiedFiles) {
    if (!this.projectId || !this.serviceName) {
      throw new Error('VirtualFileSystem not initialized with projectId/serviceName');
    }
    
    const syncedFiles = [];
    
    for (const change of modifiedFiles) {
      try {
        if (change.type === 'deleted') {
          await indexedDbFileStorage.markFileAsDeleted(
            this.projectId,
            this.serviceName,
            change.path
          );
        } else {
          // Modified or created
          await indexedDbFileStorage.saveFile(
            this.projectId,
            this.serviceName,
            change.path,
            change.newContent,
            { isModified: true }
          );
        }
        
        syncedFiles.push(change.path);
      } catch (error) {
        console.error(`Failed to sync file ${change.path}:`, error);
      }
    }
    
    return syncedFiles;
  }

  /**
   * Cleanup
   */
  cleanup() {
    this.files = {};
    this.filePaths = [];
    this.filesSnapshot = {};
    this.loadedFiles.clear();
    this.projectId = null;
    this.serviceName = null;
  }
}

export default VirtualFileSystem;
